import React, {memo, ReactNode, useMemo} from 'react'
import styled from "styled-components"
import {
  IconContainer, JustifyContentProperty, MdView,
} from "@biron-data/react-components"
import {DatabaseIcon, DocumentTextIcon, GlobeIcon, KeyIcon} from "@heroicons/react/outline";
import Language from "../../../language";
import {DatamodelRoles} from "./UserManager.types";
import {TooltipPlacement} from "antd/es/tooltip";
import {wrapWithPopover} from "../../../commons/wrapWithPopover";

interface Props {
  roles: DatamodelRoles[]
  layout: "row" | "column"
  justifyContent?: JustifyContentProperty
  tooltipPlacement?: TooltipPlacement
  popover?: ReactNode
}

// eslint-disable-next-line react/display-name
const DatamodelRights = memo<Props>(({popover,
                                       roles,
                                       layout,
                                       justifyContent,
                                       tooltipPlacement}) => {
  const hasPersonalDashboardAccess = roles?.includes(DatamodelRoles.DATAMODEL_EXPLORER)
  const hasDashboardViewerAccess = roles?.includes(DatamodelRoles.DATAMODEL_VIEWER) || hasPersonalDashboardAccess

  const resume = useMemo(() => <FlexDiv $layout={layout} $justifyContent={justifyContent}>
    {layout === "column" && <FlexDiv $layout={"row"}>
        <Category>{Language.get(`admin.datamodels.content.categoryTitle`)}</Category>
    </FlexDiv>}
    <FlexDiv $layout={"row"} $justifyContent={justifyContent}>
      <IconContainer key="dataAccess" data-testid="dataAccessIcon"
                     color={hasDashboardViewerAccess ? "var(--primary)" : "var(--medium-grey)"}><DatabaseIcon/></IconContainer>
      {
        layout === "column" && Language.get(`admin.datamodels.content.dataAccess.${hasDashboardViewerAccess ? 'can' : 'cannot'}`)
      }
    </FlexDiv>
    <FlexDiv $layout={"row"} $justifyContent={justifyContent}>
      <IconContainer key="publicDashboard" data-testid="publicDashboardIcon"
                     color={hasDashboardViewerAccess ? "var(--primary)" : "var(--medium-grey)"}><GlobeIcon/></IconContainer>
      {
        layout === "column" && Language.get(`admin.datamodels.content.publicDashboardAccess.${hasDashboardViewerAccess ? 'can' : 'cannot'}`)
      }
    </FlexDiv>
    <FlexDiv $layout={"row"} $justifyContent={justifyContent}>
      <IconContainer key="personalDashboard" data-testid="personalDashboardIcon"
                     color={hasPersonalDashboardAccess ? "var(--primary)" : "var(--medium-grey)"}><KeyIcon/></IconContainer>
      {
        layout === "column" && Language.get(`admin.datamodels.content.personalDashboardAccess.${hasPersonalDashboardAccess ? 'can' : 'cannot'}`)
      }
    </FlexDiv>
    <FlexDiv $layout={"row"} $justifyContent={justifyContent}>
      <IconContainer key="pluginsAccess" data-testid="pluginsAccessIcon"
                     color={hasPersonalDashboardAccess ? "var(--primary)" : "var(--medium-grey)"}><DocumentTextIcon/></IconContainer>
      {
        layout === "column" && <MdViewContainer onClick={(e) => e.stopPropagation()}>
              <MdView padding={0} description={Language.get(`admin.datamodels.content.pluginsAccess.${hasPersonalDashboardAccess ? 'can' : 'cannot'}`)}/>
          </MdViewContainer>
      }
    </FlexDiv>
  </FlexDiv>, [hasDashboardViewerAccess, hasPersonalDashboardAccess, justifyContent, layout])

  return layout === "row" ? wrapWithPopover(
    resume,
    popover,
    tooltipPlacement,) : resume
})

export default DatamodelRights

const FlexDiv = styled.div<{$layout: "row" | "column", $justifyContent?: JustifyContentProperty}>`
    display: flex;
    ${({$justifyContent}) => `justify-content: ${$justifyContent ?? 'start'};`}
    ${({$layout}) => `flex-direction: ${$layout};`}
    gap: 10px;
`

const Category = styled.div`
    font-weight: 500;
`

const MdViewContainer = styled.div`
    & * {
        min-height: 21px;
    }
`
